import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Smartphone, Globe, Cpu, ChevronRight, ChevronDown, MessageCircle, Mail, X } from 'lucide-react';
import FormModal from '../FormModal';
import { getWhatsAppLink, getEmailLink } from '../../lib/utils';
import { useLanguage } from '../../context/LanguageContext';

export const ServicesSection = () => {
  const [expandedService, setExpandedService] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const { t, language } = useLanguage();

  const services = {
    fr: [
      {
        id: 'applications',
        icon: Smartphone,
        title: 'Applications Mobiles & Web',
        shortDesc: 'Android, iOS, dashboards, SaaS',
        sections: [
          { title: 'Applications Mobiles', desc: 'Nous développons des applications mobiles modernes.', features: ['Android / iOS', 'Interface intuitive', 'Notifications push', 'Sécurité des données', 'Base de données cloud'] },
          { title: 'Applications Web', desc: 'Dashboards interactifs et systèmes de gestion.', features: ['Dashboard interactif', 'Gestion utilisateurs', 'Accès sécurisé', 'Responsive design'] },
          { title: 'SaaS', desc: 'Solutions logicielles en tant que service.', features: ['Comptes utilisateurs', 'Paiements intégrés', 'Multi-utilisateurs', 'Évolutivité'] }
        ]
      },
      {
        id: 'websites',
        icon: Globe,
        title: 'Sites Web',
        shortDesc: 'Vitrines, plateformes, landing pages',
        sections: [
          { title: 'Site Vitrine', desc: 'Présentation professionnelle de votre entreprise.', features: ['Design moderne', 'SEO optimisé', 'Responsive', 'Formulaires de contact'] },
          { title: 'Plateforme Web', desc: 'Systèmes complets et personnalisés.', features: ['Fonctionnalités avancées', 'Base de données', 'API intégrées', 'Administration'] },
          { title: 'Landing Page', desc: 'Pages de conversion marketing.', features: ['Optimisé conversion', 'A/B testing ready', 'Analytics intégré', 'Formulaires avancés'] }
        ]
      },
      {
        id: 'automation',
        icon: Cpu,
        title: 'Automatisation & IA',
        shortDesc: 'Workflows, chatbots, intégrations',
        sections: [
          { title: 'Automatisation', desc: 'Automatisez vos processus métier.', features: ['Workflows automatisés', 'Intégrations API', 'Notifications automatiques', 'Rapports automatisés'] },
          { title: 'Intelligence Artificielle', desc: 'Solutions IA personnalisées.', features: ['Chatbots intelligents', 'Analyse de données', 'Prédictions', 'NLP personnalisé'] },
          { title: 'Connexions', desc: 'Connectez vos outils entre eux.', features: ['Zapier-like', 'API personnalisées', 'Webhooks', 'Synchronisation temps réel'] }
        ]
      }
    ],
    en: [
      {
        id: 'applications',
        icon: Smartphone,
        title: 'Mobile & Web Apps',
        shortDesc: 'Android, iOS, dashboards, SaaS',
        sections: [
          { title: 'Mobile Applications', desc: 'We develop modern mobile applications.', features: ['Android / iOS', 'Intuitive interface', 'Push notifications', 'Data security', 'Cloud database'] },
          { title: 'Web Applications', desc: 'Interactive dashboards and management systems.', features: ['Interactive dashboard', 'User management', 'Secure access', 'Responsive design'] },
          { title: 'SaaS', desc: 'Software as a service solutions.', features: ['User accounts', 'Integrated payments', 'Multi-users', 'Scalability'] }
        ]
      },
      {
        id: 'websites',
        icon: Globe,
        title: 'Websites',
        shortDesc: 'Showcase, platforms, landing pages',
        sections: [
          { title: 'Showcase Website', desc: 'Professional presentation of your company.', features: ['Modern design', 'SEO optimized', 'Responsive', 'Contact forms'] },
          { title: 'Web Platform', desc: 'Complete and customized systems.', features: ['Advanced features', 'Database', 'Integrated APIs', 'Administration'] },
          { title: 'Landing Page', desc: 'Marketing conversion pages.', features: ['Conversion optimized', 'A/B testing ready', 'Integrated analytics', 'Advanced forms'] }
        ]
      },
      {
        id: 'automation',
        icon: Cpu,
        title: 'Automation & AI',
        shortDesc: 'Workflows, chatbots, integrations',
        sections: [
          { title: 'Automation', desc: 'Automate your business processes.', features: ['Automated workflows', 'API integrations', 'Automatic notifications', 'Automated reports'] },
          { title: 'Artificial Intelligence', desc: 'Custom AI solutions.', features: ['Smart chatbots', 'Data analysis', 'Predictions', 'Custom NLP'] },
          { title: 'Connections', desc: 'Connect your tools together.', features: ['Zapier-like', 'Custom APIs', 'Webhooks', 'Real-time sync'] }
        ]
      }
    ]
  };

  const currentServices = services[language];

  const toggleService = (id) => {
    setExpandedService(expandedService === id ? null : id);
  };

  return (
    <>
      <section id="services" className="py-24 bg-[#0B1521]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#4A90D9]/20 text-[#4A90D9] text-sm font-medium tracking-wide mb-4">
              {t('services_badge')}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
              {t('services_title')}
            </h2>
          </motion.div>

          {/* Service Cards */}
          <div className="space-y-4">
            {currentServices.map((service, index) => {
              const isExpanded = expandedService === service.id;
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  {/* Clickable Header */}
                  <button
                    onClick={() => toggleService(service.id)}
                    className={`w-full flex items-center gap-5 p-6 rounded-2xl transition-all duration-300 group cursor-pointer ${
                      isExpanded
                        ? 'bg-[#4A90D9] text-white'
                        : 'bg-[#152840] text-white/90 hover:bg-[#1a3350] border border-white/10 hover:border-[#4A90D9]/40'
                    }`}
                    data-testid={`service-tab-${service.id}`}
                  >
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                      isExpanded ? 'bg-white/20' : 'bg-[#4A90D9]/15'
                    }`}>
                      <service.icon size={28} className={isExpanded ? 'text-white' : 'text-[#4A90D9]'} />
                    </div>
                    <div className="text-left flex-1">
                      <h3 className="text-xl font-bold">{service.title}</h3>
                      <p className={`text-sm mt-0.5 ${isExpanded ? 'text-white/80' : 'text-white/50'}`}>{service.shortDesc}</p>
                    </div>
                    <div className={`flex items-center gap-2 text-sm font-medium ${isExpanded ? 'text-white/80' : 'text-[#4A90D9]'}`}>
                      <span className="hidden sm:inline">{isExpanded ? (language === 'fr' ? 'Fermer' : 'Close') : (language === 'fr' ? 'Voir les détails' : 'See details')}</span>
                      <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
                        <ChevronDown size={20} />
                      </motion.div>
                    </div>
                  </button>

                  {/* Expandable Details */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
                          {service.sections.map((section, i) => (
                            <motion.div
                              key={section.title}
                              initial={{ opacity: 0, y: 15 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: i * 0.08 }}
                              className="service-card rounded-2xl p-6"
                              data-testid={`service-card-${i}`}
                            >
                              <h4 className="text-lg font-semibold text-white mb-2">{section.title}</h4>
                              <p className="text-white/50 text-sm mb-4">{section.desc}</p>
                              <ul className="space-y-2">
                                {section.features.map((feature, fi) => (
                                  <li key={fi} className="flex items-center gap-2 text-white/80 text-sm">
                                    <ChevronRight size={14} className="text-[#4A90D9] flex-shrink-0" />
                                    {feature}
                                  </li>
                                ))}
                              </ul>
                            </motion.div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <div className="inline-flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => setShowFormModal(true)}
                className="px-8 py-4 bg-[#4A90D9] text-white rounded-full font-semibold hover:bg-[#5BA0E9] transition-colors duration-200"
                data-testid="services-cta-form"
              >
                {t('services_cta')}
              </button>
              <a
                href={getWhatsAppLink(language === 'en' ? "Hello, I'm interested in your services." : "Bonjour, je suis intéressé par vos services.")}
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-[#25D366] text-white rounded-full font-semibold hover:bg-[#20BD5A] transition-colors duration-200 flex items-center justify-center gap-2"
                data-testid="services-cta-whatsapp"
              >
                <MessageCircle size={20} />
                {t('whatsapp')}
              </a>
              <a
                href={getEmailLink(language === 'en' ? 'Quote request' : 'Demande de devis', language === 'en' ? "Hello, I'm interested in your services." : 'Bonjour, je suis intéressé par vos services.')}
                className="px-8 py-4 bg-white/10 text-white rounded-full font-semibold hover:bg-white/20 transition-colors duration-200 flex items-center justify-center gap-2 border border-white/20"
                data-testid="services-cta-email"
              >
                <Mail size={20} />
                {t('email')}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <FormModal
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        formType="project"
        title={t('services_cta')}
      />
    </>
  );
};

export default ServicesSection;
